/**
 * Per-client Bing Webmaster feeds.
 *
 * Mirrors `ClientGoogleFeedsPage.js`:
 *   1. Lists every verified site visible to the agency's master Bing
 *      API key.
 *   2. Each row has a property-assignment dropdown so the admin can
 *      tag the site to a business unit on this client (or ignore it).
 *   3. Below the assignment table, "Bing search performance" rolls up
 *      30-day + 90-day impressions/clicks plus top queries and top
 *      landing pages for every site already assigned — the same data
 *      that will feed the monthly PDF.
 *
 * The master Bing call (`GetUserSites`) is cheap so we re-fetch on
 * every mount; metrics fetches fan out (one Bing request per site per
 * panel) so we lazy-load the performance panel behind a button.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import {
  Globe, RefreshCw, Loader2, AlertTriangle, ExternalLink, ShieldCheck,
  ShieldAlert, BarChart3, ChevronDown, ChevronRight, TrendingUp, Search,
  FileText,
} from "lucide-react";

const BING_COLOR = "#008373";

export default function ClientBingFeedsPage() {
  const { clientId } = useParams();
  const [resources, setResources] = useState({ loading: true });
  const [businessUnits, setBusinessUnits] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const refresh = useCallback(async () => {
    setLoadError(null);
    try {
      const [res, bu, asg] = await Promise.all([
        api.get(`/clients/${clientId}/bing/resources`).then((r) => r.data),
        api.get(`/clients/${clientId}/business-units`)
          .then((r) => r.data).catch(() => []),
        api.get(`/clients/${clientId}/bing/resource-assignments`)
          .then((r) => r.data).catch(() => ({ assignments: {} })),
      ]);
      const list = res?.resources?.sites || [];
      setResources({ sites: list, loading: false, error: res?.error || null });
      setBusinessUnits(bu || []);
      setAssignments(asg?.assignments || {});
    } catch (e) {
      logger.warn("Bing feeds load failed", e?.response?.data);
      setLoadError(e?.response?.data?.detail || "Failed to load Bing data");
      setResources({ sites: [], loading: false });
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
    toast.success("Bing sites refreshed");
  };

  const assignResource = useCallback(
    async (resource_id, resource_label, business_unit_id, status) => {
      const key = `bing_sites::${resource_id}`;
      setAssignments((prev) => {
        const next = { ...prev };
        if (status === "unassigned") {
          delete next[key];
        } else {
          next[key] = {
            service: "bing_sites", resource_id, resource_label,
            business_unit_id: status === "assigned" ? business_unit_id : null,
            status,
          };
        }
        return next;
      });
      try {
        await api.post(`/clients/${clientId}/bing/resource-assignments`, {
          service: "bing_sites", resource_id, resource_label,
          business_unit_id: status === "assigned" ? business_unit_id : null,
          status,
        });
        toast.success(
          status === "assigned" ? "Site assigned"
          : status === "ignored" ? "Ignored — excluded from reports"
          : "Cleared",
        );
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to update");
        try {
          const { data } = await api.get(
            `/clients/${clientId}/bing/resource-assignments`);
          setAssignments(data?.assignments || {});
        } catch (_) { /* ignore */ }
      }
    },
    [clientId],
  );

  const loadMetrics = async () => {
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const { data } = await api.get(
        `/clients/${clientId}/bing/metrics/summary`);
      setMetrics(data);
    } catch (e) {
      setMetricsError(e.response?.data?.detail || "Failed to load metrics");
    } finally {
      setMetricsLoading(false);
    }
  };

  const assignedCount = useMemo(() =>
    Object.values(assignments).filter((a) => a.status === "assigned").length,
    [assignments]);

  return (
    <div className="space-y-6" data-testid="client-bing-feeds-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-overline">Connections · Bing</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Bing feeds
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Every site verified on the agency&apos;s master Bing Webmaster
            account is listed below. Tag each one to a property on this
            client and Bing&apos;s search-performance metrics (impressions,
            clicks, queries, pages) will feed the monthly report.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onRefresh}
          disabled={refreshing}
          className="rounded-sm" data-testid="bing-feeds-refresh">
          {refreshing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {loadError && (
        <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5 shrink-0" />
          {loadError}
        </div>
      )}

      {/* ── Bing sites card ────────────────────────────────────────── */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="h-1.5" style={{ background: BING_COLOR }} />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-sm flex items-center justify-center"
                style={{ background: `${BING_COLOR}14` }}>
                <Globe className="h-5 w-5" style={{ color: BING_COLOR }} />
              </div>
              <div>
                <h2 className="font-display font-bold text-xl">
                  Master Bing sites ({(resources.sites || []).length})
                </h2>
                <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">
                  Sites pulled from <span className="font-mono">GetUserSites</span>
                  {" "}on the agency master API key. Assignments persist on this
                  client only — they don&apos;t affect other clients.
                </p>
              </div>
            </div>
            <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 font-mono text-[10px]"
              data-testid="bing-assigned-count">
              {assignedCount} assigned
            </Badge>
          </div>

          {resources.loading ? (
            <div className="text-[12px] text-zinc-500 flex items-center gap-2 py-3">
              <Loader2 className="h-4 w-4 animate-spin" /> Discovering Bing sites…
            </div>
          ) : resources.error ? (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <strong>Couldn&apos;t reach Bing.</strong> {resources.error}
              <div className="mt-1 text-[11px]">
                Check the agency API key on{" "}
                <Link to="/integrations/microsoft" className="underline">
                  /integrations/microsoft
                </Link>.
              </div>
            </div>
          ) : (resources.sites || []).length === 0 ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-6 text-center text-[12px] text-zinc-600">
              No verified Bing sites yet. Add a site at{" "}
              <a href="https://www.bing.com/webmasters/home"
                target="_blank" rel="noreferrer" className="underline">
                bing.com/webmasters
              </a>{" "}
              and verify it on the agency account.
            </div>
          ) : (
            <div className="rounded-sm border border-zinc-200 overflow-hidden">
              <div className="grid grid-cols-[1fr_120px_240px] gap-2 px-3 py-2 border-b border-zinc-100 bg-zinc-50/50 label-overline text-[10px]">
                <div>Site URL</div>
                <div>Verified</div>
                <div>Assignment</div>
              </div>
              <div className="divide-y divide-zinc-100">
                {(resources.sites || []).map((s) => {
                  const aKey = `bing_sites::${s.site_url}`;
                  const asg = assignments[aKey];
                  const selValue = asg?.status === "ignored"
                    ? "__ignored__"
                    : asg?.business_unit_id || "";
                  return (
                    <div key={s.site_url}
                      className="grid grid-cols-[1fr_120px_240px] gap-2 px-3 py-2 items-center"
                      data-testid={`bing-site-${s.site_url}`}>
                      <a href={s.site_url} target="_blank" rel="noreferrer"
                        className="font-mono text-[12px] truncate hover:underline"
                        title={s.site_url}>
                        {s.site_url} <ExternalLink className="h-2.5 w-2.5 inline" />
                      </a>
                      <div>
                        {s.is_verified ? (
                          <Badge className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 font-mono text-[10px] gap-1">
                            <ShieldCheck className="h-3 w-3" /> verified
                          </Badge>
                        ) : (
                          <Badge className="rounded-sm bg-amber-50 text-amber-800 border-amber-200 font-mono text-[10px] gap-1">
                            <ShieldAlert className="h-3 w-3" /> pending
                          </Badge>
                        )}
                      </div>
                      <PropertySelect
                        value={selValue}
                        businessUnits={businessUnits}
                        onChange={(v) => {
                          if (v === "") {
                            assignResource(s.site_url, s.site_url, null, "unassigned");
                          } else if (v === "__ignored__") {
                            assignResource(s.site_url, s.site_url, null, "ignored");
                          } else {
                            assignResource(s.site_url, s.site_url, v, "assigned");
                          }
                        }}
                        testid={`bing-assign-${s.site_url}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Performance metrics card ───────────────────────────────── */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden">
        <div className="h-1.5" style={{ background: BING_COLOR }} />
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-sm flex items-center justify-center"
                style={{ background: `${BING_COLOR}14` }}>
                <BarChart3 className="h-5 w-5" style={{ color: BING_COLOR }} />
              </div>
              <div>
                <h2 className="font-display font-bold text-xl">
                  Bing search performance
                </h2>
                <p className="text-[12px] text-zinc-500 mt-0.5 max-w-2xl">
                  Live snapshot from Bing for every assigned site —
                  30-day + 90-day impressions/clicks, top 10 queries, and top
                  10 landing pages. Same data the monthly PDF will use.
                </p>
              </div>
            </div>
            <Button size="sm" onClick={loadMetrics}
              disabled={metricsLoading || assignedCount === 0}
              className="rounded-sm text-white"
              style={{ background: BING_COLOR }}
              data-testid="bing-load-metrics"
              title={assignedCount === 0
                ? "Assign at least one site first" : "Fetch live metrics"}>
              {metricsLoading
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <TrendingUp className="h-3.5 w-3.5 mr-1.5" />}
              {metrics ? "Refresh metrics" : "Load metrics"}
            </Button>
          </div>

          {metricsError && (
            <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-[12px] text-red-900">
              {metricsError}
            </div>
          )}

          {assignedCount === 0 ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-5 text-[12px] text-zinc-600">
              Assign at least one Bing site to a property above to start
              pulling search-performance metrics.
            </div>
          ) : !metrics ? (
            <div className="rounded-sm border border-dashed border-zinc-200 bg-zinc-50/40 p-5 text-[12px] text-zinc-600">
              Click <strong>Load metrics</strong> to fan out to Bing —
              one <span className="font-mono">GetRankAndTrafficStats</span>,
              <span className="font-mono">GetQueryStats</span> and
              <span className="font-mono">GetPageStats</span> call per assigned
              site. Takes a few seconds.
            </div>
          ) : (
            <div className="space-y-4">
              {(metrics.sites || []).map((s) => (
                <SitePerformanceCard key={s.site_url} site={s}
                  businessUnits={businessUnits} />
              ))}
              <div className="text-[10px] font-mono text-zinc-400 pt-1">
                Fetched {new Date(metrics.fetched_at).toLocaleString()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SitePerformanceCard({ site, businessUnits }) {
  const [open, setOpen] = useState(true);
  const buName = businessUnits.find(
    (b) => b.business_unit_id === site.business_unit_id)?.name;
  const errors = site.errors || {};
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div className="rounded-sm border border-zinc-200 overflow-hidden"
      data-testid={`bing-perf-${site.site_url}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full px-3 py-2.5 bg-zinc-50/60 hover:bg-zinc-100 flex items-center gap-2 text-left">
        {open
          ? <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 text-zinc-500 shrink-0" />}
        <Globe className="h-3.5 w-3.5 shrink-0" style={{ color: BING_COLOR }} />
        <span className="font-mono text-[12px] font-semibold truncate">
          {site.site_url}
        </span>
        {buName && (
          <span className="text-[10px] font-mono text-zinc-500 truncate">
            · {buName}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 text-[11px] font-mono text-zinc-700 shrink-0">
          <span title="30-day impressions">
            <strong>{(site.totals_30d?.impressions || 0).toLocaleString()}</strong> impr
          </span>
          <span title="30-day clicks">
            <strong>{(site.totals_30d?.clicks || 0).toLocaleString()}</strong> clicks
          </span>
        </div>
      </button>
      {open && (
        <div className="p-3 space-y-3">
          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Tile label="Impressions · 30d" value={site.totals_30d?.impressions} />
            <Tile label="Clicks · 30d" value={site.totals_30d?.clicks} />
            <Tile label="Impressions · 90d" value={site.totals_90d?.impressions} />
            <Tile label="Clicks · 90d" value={site.totals_90d?.clicks} />
          </div>

          {hasErrors && (
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900 font-mono">
              {Object.entries(errors).map(([k, v]) => (
                <div key={k}><strong>{k}:</strong> {v}</div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <PerformanceList title="Top queries" icon={Search}
              items={site.top_queries || []} />
            <PerformanceList title="Top landing pages" icon={FileText}
              items={site.top_pages || []} truncate />
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white p-2">
      <div className="label-overline text-[9px]">{label}</div>
      <div className="font-display font-bold text-lg tracking-tight mt-0.5">
        {(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function PerformanceList({ title, icon: Icon, items, truncate }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white">
      <div className="px-3 py-2 border-b border-zinc-100 flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-zinc-500" />
        <span className="label-overline text-[10px]">{title}</span>
        <span className="text-[10px] font-mono text-zinc-400 ml-auto">
          ({items.length})
        </span>
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-3 text-[11px] italic text-zinc-500">
          No data yet.
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          {items.map((it, i) => (
            <div key={it.term || i}
              className="px-3 py-1.5 grid grid-cols-[1fr_60px_50px] gap-2 items-center text-[11px]">
              <span className={`font-mono ${truncate ? "truncate" : ""}`}
                title={it.term}>
                {it.term}
              </span>
              <span className="font-mono text-zinc-700 text-right">
                {(it.impressions || 0).toLocaleString()}
              </span>
              <span className="font-mono text-zinc-700 text-right">
                {(it.clicks || 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PropertySelect({ value, businessUnits, onChange, testid }) {
  const isIgnored = value === "__ignored__";
  return (
    <select value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`h-7 w-full text-[11px] font-mono rounded-sm border px-1.5 truncate ${
        isIgnored
          ? "border-zinc-300 bg-zinc-100 text-zinc-500"
          : value
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-zinc-200 bg-white text-zinc-700"
      }`}>
      <option value="">— Unassigned —</option>
      <optgroup label="Assign to property">
        {(businessUnits || []).map((b) => (
          <option key={b.business_unit_id} value={b.business_unit_id}>
            {b.name || b.business_unit_id}
          </option>
        ))}
      </optgroup>
      <option value="__ignored__">Ignore (exclude from reports)</option>
    </select>
  );
}
